import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  QuestionMarkCircleIcon,
  ShoppingCartIcon,
  CubeIcon,
  UserGroupIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  TruckIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/solid';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { siteConfigAPI } from '../services/api';

/* ─── Icon map (backend stores slug strings) ─── */
const ICON_MAP: Record<string, React.ElementType> = {
  'question-mark-circle': QuestionMarkCircleIcon,
  'cube': CubeIcon,
  'user-group': UserGroupIcon,
  'shopping-cart': ShoppingCartIcon,
  'truck': TruckIcon,
  'chart-bar': ChartBarIcon,
  'credit-card': CreditCardIcon,
  'cog-6-tooth': Cog6ToothIcon,
  'shield-check': ShieldCheckIcon,
  'lifebuoy': LifebuoyIcon,
};

/* ─── Types matching API response ─── */
interface FAQ {
  id: number;
  question: string;
  answer: string;
}

interface Topic {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  faqs: FAQ[];
}

/* ─── Accordion item ─── */
const FAQItem: React.FC<{ faq: FAQ; isOpen: boolean; toggle: () => void }> = ({ faq, isOpen, toggle }) => (
  <div style={{ borderBottom: '1px solid #e2e8f0' }}>
    <button
      onClick={toggle}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.95rem', paddingRight: '12px' }}>{faq.question}</span>
      {isOpen
        ? <ChevronUpIcon style={{ width: 18, height: 18, color: '#94a3b8', flexShrink: 0 }} />
        : <ChevronDownIcon style={{ width: 18, height: 18, color: '#94a3b8', flexShrink: 0 }} />}
    </button>
    {isOpen && (
      <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.7, padding: '0 0 16px', margin: 0 }}>
        {faq.answer}
      </p>
    )}
  </div>
);

/* ─── Main component ─── */
const HelpCenter: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedTopic, setExpandedTopic] = useState<number | null>(null);
  const [openFAQs, setOpenFAQs] = useState<Record<string, boolean>>({});

  useEffect(() => {
    document.title = 'Help Center | Sourdoughsy';
    siteConfigAPI.getHelpTopicsPublic()
      .then(res => setTopics(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const detailRef = useRef<HTMLDivElement>(null);

  /* Smooth-scroll to the detail panel whenever a topic is selected */
  useEffect(() => {
    if (expandedTopic && detailRef.current) {
      setTimeout(() => {
        detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [expandedTopic]);

  const toggleFAQ = (key: string) => setOpenFAQs(prev => ({ ...prev, [key]: !prev[key] }));

  const lower = search.toLowerCase().trim();
  const filtered = lower
    ? topics.map(t => ({
        ...t,
        faqs: t.faqs.filter(f => f.question.toLowerCase().includes(lower) || f.answer.toLowerCase().includes(lower)),
      })).filter(t => t.faqs.length > 0 || t.title.toLowerCase().includes(lower) || t.description.toLowerCase().includes(lower))
    : topics;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar solid />

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
        paddingTop: '120px', paddingBottom: '48px', textAlign: 'center',
      }}>
        <Container>
          <h1 style={{ color: 'white', fontWeight: 800, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', letterSpacing: '-0.5px', marginBottom: '8px' }}>
            Help Center
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', marginBottom: '28px' }}>
            Find answers to common questions about Sourdoughsy
          </p>

          {/* Search */}
          <div style={{ maxWidth: '520px', margin: '0 auto', position: 'relative' }}>
            <MagnifyingGlassIcon style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: 20, height: 20, color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search for a question..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '14px 16px 14px 48px', borderRadius: '12px', border: 'none',
                fontSize: '0.95rem', outline: 'none', background: 'rgba(255,255,255,0.1)',
                color: 'white', backdropFilter: 'blur(8px)',
              }}
            />
          </div>
        </Container>
      </div>

      {/* Content */}
      <div style={{ flex: 1, background: '#f8fafc', padding: '48px 0 80px' }}>
        <Container style={{ maxWidth: '960px' }}>

          {/* Quick links */}
          <div className="text-center mb-4" style={{ fontSize: '0.85rem', color: '#64748b' }}>
            Looking for a step-by-step guide? Check our{' '}
            <Link to="/docs" style={{ color: '#3b82f6', fontWeight: 600 }}>User Guide</Link>
          </div>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
          ) : topics.length === 0 ? (
            <div className="text-center py-5" style={{ color: '#94a3b8' }}>No help topics available yet.</div>
          ) : (
            <>
              {/* Topic grid (when NOT searching) */}
              {!lower && (
                <Row className="g-3 mb-4">
                  {topics.map(t => {
                    const Icon = ICON_MAP[t.icon] || QuestionMarkCircleIcon;
                    return (
                      <Col sm={6} lg={4} key={t.id}>
                        <button
                          onClick={() => setExpandedTopic(expandedTopic === t.id ? null : t.id)}
                          style={{
                            width: '100%', background: 'white', border: expandedTopic === t.id ? `2px solid ${t.color}` : '2px solid transparent',
                            borderRadius: '14px', padding: '20px', textAlign: 'left', cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'all 0.15s',
                          }}
                        >
                          <div style={{ width: 40, height: 40, borderRadius: '10px', background: `${t.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                            <Icon style={{ width: 22, height: 22, color: t.color }} />
                          </div>
                          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1f2937', marginBottom: '4px' }}>{t.title}</h3>
                          <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 0, lineHeight: 1.4 }}>{t.description}</p>
                        </button>
                      </Col>
                    );
                  })}
                </Row>
              )}

              {/* Expanded topic or search results */}
              {(expandedTopic || lower) && (
                <div ref={detailRef} style={{ scrollMarginTop: '24px' }}>
                  {filtered.map(t => {
                    if (!lower && t.id !== expandedTopic) return null;
                    const Icon = ICON_MAP[t.icon] || QuestionMarkCircleIcon;
                    return (
                      <div key={t.id} style={{
                        background: 'white', borderRadius: '16px', padding: 'clamp(20px, 4vw, 32px)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '16px',
                      }}>
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <div style={{ width: 36, height: 36, borderRadius: '8px', background: `${t.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon style={{ width: 20, height: 20, color: t.color }} />
                          </div>
                          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1f2937', marginBottom: 0 }}>{t.title}</h2>
                        </div>
                        {t.faqs.length > 0 ? (
                          t.faqs.map((faq, i) => {
                            const key = `${t.id}-${i}`;
                            return <FAQItem key={key} faq={faq} isOpen={!!openFAQs[key] || !!lower} toggle={() => toggleFAQ(key)} />;
                          })
                        ) : (
                          <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No matching questions in this topic.</p>
                        )}
                      </div>
                    );
                  })}
                  {filtered.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                      <MagnifyingGlassIcon style={{ width: 48, height: 48, color: '#cbd5e1', marginBottom: '12px' }} />
                      <p style={{ color: '#64748b', fontSize: '1rem' }}>No results found for "<strong>{search}</strong>"</p>
                      <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                        Try a different search or <Link to="/contact" style={{ color: '#3b82f6' }}>contact us</Link>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Bottom CTA */}
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center', marginTop: '32px',
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>
              Still need help?
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '16px' }}>
              Can't find what you're looking for? Reach out to our team.
            </p>
            <Link
              to="/contact"
              style={{
                display: 'inline-block', background: '#3b82f6', color: 'white', padding: '10px 24px',
                borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none',
              }}
            >
              Contact Us
            </Link>
          </div>

        </Container>
      </div>

      <PublicFooter />
    </div>
  );
};

export default HelpCenter;
